// What each of the eleven kinds does, as one small invented input and the output it gives. Shown collapsed at the
// top of the kind's editor. The data never comes from a run.
import type {
  AggregateServiceModel,
  BatchServiceModel,
  FilterServiceModel,
  IServiceModel,
  JoinServiceModel,
} from '@/types'

/** One block of an example pane. It holds JSON documents, or plain lines, or a sentence where a row gives nothing. */
export interface ExampleBlock {
  /** A small heading over the block, such as "Batch 1 of 2". */
  title?: string
  /** JSON documents, drawn one under another. */
  docs?: unknown[]
  /** Text that is not JSON, such as the rows of a file. */
  lines?: string[]
  note?: string
}

export interface ServiceExampleData {
  input: ExampleBlock[]
  output: ExampleBlock[]
}

const EMPLOYEES = [
  { employeeNumber: 'E-1041', costCentre: 'CC-310' },
  { employeeNumber: 'E-1042', costCentre: 'CC-310' },
  { employeeNumber: 'E-1043', costCentre: 'CC-420' },
]
const REPORT_ROWS = [
  { reportId: 'R-88', employeeNumber: 'E-1042' },
  { reportId: 'R-91', employeeNumber: 'E-2000' },
]
const REPORTS = {
  reports: [
    {
      reportId: 'R-88',
      currency: 'EUR',
      entries: [
        {
          entryId: 'N-1',
          amount: 30,
          allocations: [
            { costCentre: 'CC-310', percent: 60 },
            { costCentre: 'CC-420', percent: 40 },
          ],
        },
        { entryId: 'N-2', amount: 12, allocations: [] },
      ],
    },
  ],
}
const POSTED = [
  { postingId: 'FP-5521', postedAmount: 10 },
  { postingId: 'FP-5522', postedAmount: 20 },
  { postingId: 'FP-5523', postedAmount: 30 },
]
const REDUCED = { Sum: 60, Count: 3, Min: 10, Max: 30, Avg: 20 }

function batchExample(model: BatchServiceModel): ServiceExampleData {
  const maxRows = model.maxRows ?? 0
  const size = maxRows > 0 ? maxRows : EMPLOYEES.length
  const batches: (typeof EMPLOYEES)[] = []
  for (let start = 0; start < EMPLOYEES.length; start += size) {
    batches.push(EMPLOYEES.slice(start, start + size))
  }
  return {
    input: [{ docs: [{ employees: EMPLOYEES, total: 3 }] }],
    output: batches.map((batch, index) => ({ title: `Batch ${index + 1} of ${batches.length}`, docs: [batch] })),
  }
}

function joinExample(model: JoinServiceModel): ServiceExampleData {
  const unmatchedRow: ExampleBlock =
    model.include === 'Everything'
      ? { title: 'R-91', docs: [{ found: false, item: null }] }
      : { title: 'R-91', note: 'No match, so the row stops here.' }
  return {
    input: [
      { title: 'Rows', docs: REPORT_ROWS },
      { title: 'In', docs: [EMPLOYEES] },
    ],
    output: [{ title: 'R-88', docs: [{ found: true, item: EMPLOYEES[1] }] }, unmatchedRow],
  }
}

function filterExample(model: FilterServiceModel): ServiceExampleData {
  const input: ExampleBlock[] = [
    { title: 'Rows', docs: REPORT_ROWS },
    { title: 'In', docs: [['E-2000', 'E-3107']] },
  ]
  if (model.include === 'Unmatched') {
    return {
      input,
      output: [
        { title: 'R-88', docs: [{ found: false, item: null }] },
        { title: 'R-91', note: 'Found, so the row stops here.' },
      ],
    }
  }
  return {
    input,
    output: [
      { title: 'R-88', note: 'Not found, so the row stops here.' },
      { title: 'R-91', docs: [{ found: true, item: 'E-2000' }] },
    ],
  }
}

function aggregateExample(model: AggregateServiceModel): ServiceExampleData {
  return {
    input: [{ title: '3 transactions of Post line', docs: POSTED }],
    output: [{ title: model.operation, docs: [REDUCED[model.operation]] }],
  }
}

const FLATTEN: ServiceExampleData = {
  input: [{ docs: [REPORTS] }],
  output: [
    {
      title: '3 rows',
      docs: [
        { report: { reportId: 'R-88' }, entry: { entryId: 'N-1', amount: 30 }, allocation: { costCentre: 'CC-310', percent: 60 } },
        { report: { reportId: 'R-88' }, entry: { entryId: 'N-1', amount: 30 }, allocation: { costCentre: 'CC-420', percent: 40 } },
        { report: { reportId: 'R-88' }, entry: { entryId: 'N-2', amount: 12 } },
      ],
    },
  ],
}

const GROUP: ServiceExampleData = {
  input: [
    {
      docs: [
        {
          lines: [
            { paymentId: 'P-1', method: 'CHK', invoiceNumber: 'INV-1', amount: 10 },
            { paymentId: 'P-1', method: 'CHK', invoiceNumber: 'INV-2', amount: 20 },
            { paymentId: 'P-2', method: 'TRF', invoiceNumber: 'INV-3', amount: 30 },
          ],
          batchRef: 'PB-2291',
        },
      ],
    },
  ],
  output: [
    {
      title: '2 groups',
      docs: [
        {
          key: 'P-1',
          head: { method: 'CHK' },
          items: [
            { invoiceNumber: 'INV-1', amount: 10 },
            { invoiceNumber: 'INV-2', amount: 20 },
          ],
        },
        { key: 'P-2', head: { method: 'TRF' }, items: [{ invoiceNumber: 'INV-3', amount: 30 }] },
      ],
    },
  ],
}

const CONCAT: ServiceExampleData = {
  input: [
    { title: 'Part 1', lines: ['H,EXPENSES,2026-10-02T09:30:00Z'] },
    { title: 'Part 2', lines: ['P-1,CHK,30', 'P-2,TRF,30'] },
    { title: 'Part 3', lines: ['T,2,60'] },
  ],
  output: [{ title: 'One document', lines: ['H,EXPENSES,2026-10-02T09:30:00Z', 'P-1,CHK,30', 'P-2,TRF,30', 'T,2,60'] }],
}

const SUB_WORKFLOW: ServiceExampleData = {
  input: [{ title: 'Inputs handed to the chosen workflow', docs: [{ reportId: 'R-88', costCentre: 'CC-310' }] }],
  output: [{ title: 'The value at its Return', docs: [{ postingId: 'FP-5521', postedAmount: 30 }] }],
}

const REJOIN: ServiceExampleData = {
  input: [{ title: '2 transactions of Build payment row', docs: ['P-1,CHK,30\n', 'P-2,TRF,30\n'] }],
  output: [{ title: 'One list', docs: [['P-1,CHK,30\n', 'P-2,TRF,30\n']] }],
}

const WAIT: ServiceExampleData = {
  input: [{ title: '3 transactions of Post line', docs: POSTED }],
  output: [{ title: 'One transaction', note: 'No output. The services after it run once.' }],
}

const RETURN: ServiceExampleData = {
  input: [{ title: '1 transaction of Total posted', docs: [60] }],
  output: [{ title: 'Output of the service that called this workflow', docs: [60] }],
}

/** The example for a service model, or undefined for a kind that has none. */
export function serviceExample(model: IServiceModel): ServiceExampleData | undefined {
  switch (model.$type) {
    case 'batch':
      return batchExample(model as BatchServiceModel)
    case 'flatten':
      return FLATTEN
    case 'group':
      return GROUP
    case 'join':
      return joinExample(model as JoinServiceModel)
    case 'filter':
      return filterExample(model as FilterServiceModel)
    case 'concat':
      return CONCAT
    case 'aggregate':
      return aggregateExample(model as AggregateServiceModel)
    case 'subWorkflow':
      return SUB_WORKFLOW
    case 'rejoin':
      return REJOIN
    case 'wait':
      return WAIT
    case 'return':
      return RETURN
    default:
      return undefined
  }
}
